import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Page, { PageBreadcrumbs } from "~/components/Page";
import { Button } from "~/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { CircleHelp, Home } from "lucide-react";
import styles from "./styles.module.scss";
import paths from "~/paths";
import docStore from "~/stores/document";
import authStore from "~/stores/auth";
import { UserPermission } from "~/user";
import {
  APIDocPayload,
  DocPayload,
  DocPayloadForEdit,
  Document,
  DocSeriesId,
  EditableDocPayload,
  isDocFieldSingle,
} from "~/document";
import { useMatch, useNavigate, useParams } from "react-router";
import FileDropArea from "./FileUploadArea";
import ConfirmationModal from "./ConfirmationModal";
import DocumentForm, { SingleFormProps, SingleFormValue, SingleOnChangeKeys } from "~/components/DocumentForm";
import RelatedDocumentsSelect from "./RelatedDocumentsSelect";
import tagStore from "~/stores/tags";
import DocumentLangVersionSelect from "./DocumentLangVersionSelect";
import { assert, dateToAPIString, reverseMap, withLoading } from "~/util";
import { useBooleanHash, useModal } from "~/util/hooks";
import cn from "classnames";
import templateStore from "~/stores/templates";
import { field, validate, ValidationSchema, VErrors } from "~/validation";
import { toJS } from "mobx";
import { apiErrorDetails, consumeLastApiErrorDetails, FieldValidationError, ReqStatus } from "~/api";
import { observer } from "mobx-react";
import HelpModal from "./HelpModal";
import UploadPageSkeleton from "./UploadPageSkeleton";
import WithAutoScrollingWrapper from "~/components/WithAutoScrollingWrapper";
import moment from "moment";
import { ToastNotificationContext } from "~/components/ToastNotificationContext";

const DOC_TYPES_TO_SELECT_FOR_DOCUMENT_CREATION = 2;

type MappedKey = keyof EditableDocPayload | (keyof EditableDocPayload)[] | null;

type AllKeys = keyof SingleFormValue;
type TT = { [P in AllKeys]: MappedKey };
const formToDPMapping: TT = {
  docTypes: "types",
  topics: "topics",
  owners: "owner",
  third_types: "third_type",
  offices: "office",
  languages: "language",
  access: "access",
  protocol: "protocol",
  expiration_date: "expiration_date",
  release_date: "release_date",
  organizational_units: "organizational_unit",
  publication_date: "publication_date",
  tags: "tags",
  title: "title",
  author: "author",
  countries: "countries",
  projects: "projects",
  abstract: "abstract",
  comment: "comment",
  pdf_option: "pdf_option",
  number: "number",
  meeting_codes: "meeting_codes",
  custom: null,
  last_modified_user: null,
  instance: "instance",
};

const dpToFormMapping: Map<keyof EditableDocPayload, keyof SingleFormValue> = reverseMap(formToDPMapping);

export const docPayloadFromFormChange = (
  docPayload: EditableDocPayload,
  key: SingleOnChangeKeys,
  value: Set<number | string> | Array<string | number> | string | number
): EditableDocPayload => {
  const np: EditableDocPayload = { ...docPayload };
  if (!docPayload.types?.includes(20)) {
    np.instance = undefined;
  }

  const mk = formToDPMapping[key];
  const mappedKey: MappedKey = mk;

  if (mappedKey === null) return np;
  if (mappedKey instanceof Array) {
    assert(value instanceof Array || value instanceof Set);
    let valArr = Array.from(value);
    for (let i = 0; i < Math.min(valArr.length, mappedKey.length); ++i) {
      const k: keyof EditableDocPayload = mappedKey[i];
      (np[k] as EditableDocPayload[typeof k]) = valArr[i];
    }
  } else {
    let val = value instanceof Set ? Array.from(value) : value;
    const isSingle = isDocFieldSingle(mappedKey);

    if (isSingle) {
      (np[mappedKey] as EditableDocPayload[typeof mappedKey]) = val instanceof Array ? val[0] : val;
    } else {
      (np[mappedKey] as EditableDocPayload[typeof mappedKey]) = val;
    }
  }
  return np;
};

const apiFieldLabels: Record<string, string> = {
  publication_date: "Publication date",
  expiration_date: "Expiration date",
  release_date: "Release date",
  title: "Title",
  type: "Document type",
  secondary_type: "Document type",
  topics: "Topics",
  author: "Author",
  access: "Access",
  language: "Language",
  owner: "Owner",
  office: "Office",
  organizational_unit: "Organizational unit",
  countries: "Countries",
  projects: "Projects",
  tags: "Tags",
  abstract: "Abstract",
  comment: "Comment",
  number: "Number",
  meeting_codes: "Meeting codes",
  protocol: "Protocol",
  pdf_option: "PDF option",
  instance: "Instance",
  third_type: "Third type",
};

const formatApiFieldKey = (key: string) =>
  apiFieldLabels[key] ?? key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const getFieldsFromValidationError = (err: FieldValidationError): string[] => {
  const labels = new Set<string>();
  for (const key of Object.keys(err.fieldErrors)) {
    labels.add(formatApiFieldKey(key));
  }
  return Array.from(labels);
};

const dpKeyToFormPropKey = (key: keyof EditableDocPayload): keyof SingleFormValue => {
  return dpToFormMapping.get(key)!;
};

const useFormPropsFromState = (
  docPayload: EditableDocPayload,
  validationErrors: VErrors<UploadFormState>,
  editing: boolean
) => {
  const defaultProps = {
    title: {},
    publication_date: {
      disabled: editing,
    },
    release_date: {
      disabled: editing,
    },
    expiration_date: {
      startDate: docPayload.date_added ? new Date(docPayload.date_added) : undefined,
    },
    protocol: {
      disabled: editing,
    },
  };
  const [props, setProps] = useState<SingleFormProps>(defaultProps);
  const [touched, setTouched] = useState<SingleFormProps>({});
  const errors = useMemo(() => validationErrors.get("docPayload"), [validationErrors]);

  const onBlur = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  useEffect(() => {
    if (!errors) return;
    const updatedProps = { ...defaultProps };
    for (const [key, err] of errors.entries()) {
      const kkey = key as keyof EditableDocPayload;
      const fkey = dpKeyToFormPropKey(kkey);
      if (!(key in updatedProps)) {
        updatedProps[fkey] = {};
      }
      if (updatedProps[fkey].disabled) continue;
      const msg = (err as any).join(", ");
      updatedProps[fkey].error = true;
      updatedProps[fkey].invalid = !!touched[fkey];
      updatedProps[fkey].invalidText = msg;
      updatedProps[fkey].onBlur = () => onBlur(fkey);
    }
    setProps(updatedProps);
  }, [errors, touched, docPayload, editing]);

  return errors ? props : defaultProps;
};

interface UploadFormState {
  docPayload: EditableDocPayload;
  file: File | null;
}

const uploadSchema: ValidationSchema<UploadFormState> = {
  docPayload: {
    title: field("title").string().required(),
    language: field("language").string().required(),
    topics: field("topics").array().required("You need to select at least one topic"),
    author: field("author").string().required(),
    publication_date: field("publication_date").date().required().onlyFuture(),
    expiration_date: field("expiration_date").date().required(),
    types: field("types").array().required("You need to specify at least one document type"),
    access: field("access").string().required("You need to specify the access level"),
  },
  file: field("file").file().required("You need to select a file").notNull(),
};

const editSchema = {
  ...uploadSchema,
  docPayload: {
    ...uploadSchema.docPayload,
    publication_date: field("publication_date").date(),
    release_date: field("release_date").date(),
    expiration_date: field("expiration_date").date(),
  },
};

export const formValueFromState = (docPayload: EditableDocPayload): SingleFormValue => {
  const d = docPayload;
  let organizational_units = [];
  if (d.organizational_unit) {
    if (typeof d.organizational_unit === "object") {
      if (d.organizational_unit.id != null) {
        organizational_units.push(d.organizational_unit.id);
      }
    } else {
      organizational_units.push(d.organizational_unit);
    }
  }
  // The API returns the series as { id, label } but takes a plain id back, so the
  // form value holds the id either way.
  const third_types: DocSeriesId[] = [];
  if (d.third_type) {
    third_types.push(typeof d.third_type === "object" ? d.third_type.id : d.third_type);
  }
  const res = {
    ...docPayload,
    third_types,
    topics: new Set(docPayload.topics),
    tags: docPayload.tags ?? [],
    countries: docPayload.countries ?? [],
    docTypes: new Set(docPayload.types),
    projects: d.projects ?? [],
    languages: d.language !== null && d.language !== undefined ? [d.language] : [],
    access: d.access !== null && d.access !== undefined ? [d.access] : [],
    owners: d.owner !== null && d.owner !== undefined ? [d.owner?.id] : [],
    offices: d.office !== null && d.office !== undefined ? [d.office] : [],
    divisions: d.division !== null && d.division !== undefined ? [d.division] : [],
    organizational_units,
    expiration_date:
      d.expiration_date === null ? null : d.expiration_date !== undefined ? new Date(d.expiration_date) : undefined,
    publication_date: d.publication_date ? new Date(d.publication_date) : undefined,
    release_date: d.release_date ? new Date(d.release_date) : undefined,
    instances: [],
    meeting_codes: d.meeting_codes || [],
  };
  return res as any;
};

export const isEditing = (): [string | undefined, boolean] => {
  const params = useParams();
  return [params.id, params.id !== undefined];
};

const compareDocPayloads = (originalDocument: EditableDocPayload, docPayload: EditableDocPayload) => {
  return Object.fromEntries(
    Object.entries(docPayload).filter(([key, value]) => {
      type K = keyof typeof originalDocument;
      const kkey = key as K;
      const origValue = originalDocument[kkey] as any;
      if (value instanceof Date) {
        return new Date(origValue).getTime() !== value.getTime();
      }
      return originalDocument[kkey] !== value;
    })
  );
};

const docPayloadFromEditable = (edp: EditableDocPayload): DocPayload => {
  return edp;
};

const defaultDocPayload = {
  publication_date: moment().toISOString(),
};

const UploadPage = observer(() => {
  const [editedId, editing] = isEditing();
  const navigate = useNavigate();

  // Permission check for editing
  const hasUploadPermission = authStore.hasPermission(UserPermission.UploadDoc);
  const hasChangePermission = authStore.hasPermission(UserPermission.ChangeDoc);

  const [originalDocument, setOriginalDocument] = useState<Document | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>();

  const [docPayload, setDocPayload] = useState<EditableDocPayload>(defaultDocPayload);
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const syncedProtoRef = useRef<string | null>(null);

  const { showNotificationWithTimeout, setNotificationData } = useContext(ToastNotificationContext);

  const clear = () => {
    syncedProtoRef.current = null;
    setOriginalDocument(null);
    setOriginalFile(null);
    setDocPayload(defaultDocPayload);
    setFile(null);
  };

  useEffect(() => {
    clear();
  }, [editing]);

  const fileHasChanged = originalFile !== file;

  const onlyUpdatedFields: EditableDocPayload = useMemo(() => {
    if (!editing || originalDocument === null) return {};
    return compareDocPayloads(originalDocument, docPayload);
  }, [docPayload, originalDocument, editing]);

  const prepareForUpload = async (payload: EditableDocPayload): Promise<APIDocPayload> => {
    const { meeting_codes, instance, types, type, secondary_type, ...dp } = payload;

    let res: APIDocPayload = {
      ...dp,
      expiration_date: dp.expiration_date ? dateToAPIString(dp.expiration_date) : undefined,
      publication_date: dp.publication_date ? dateToAPIString(dp.publication_date) : undefined,
      release_date: dp.release_date ? dateToAPIString(dp.release_date) : undefined,
    };
    if (dp.expiration_date === null) {
      res.expiration_date = null;
    }
    if (types) {
      res.type = types?.[0] || null;
      res.secondary_type = types?.[1] || null;
    }

    return res;
  };

  const onApiError = () => {
    setNotificationData({
      title: "Document was not uploaded due to some API error",
      kind: "error",
      details: consumeLastApiErrorDetails(),
    });
    showNotificationWithTimeout();
  };

  const onFieldsMissing = (missingFields: string[], details?: string) => {
    setNotificationData({
      title: "Document was not uploaded, some required fields are missing",
      subtitle: missingFields.length > 0 ? `Missing: ${missingFields.join(", ")}` : "",
      kind: "error",
      details,
    });
    showNotificationWithTimeout();
  };

  const upload = async () => {
    if (editing) {
      const payload: DocPayloadForEdit = await prepareForUpload({
        ...onlyUpdatedFields,
        types: docPayload.types,
      });
      const doc = await docStore.edit(originalDocument!.protocol, payload, fileHasChanged ? file : null);
      if (doc instanceof FieldValidationError) {
        onFieldsMissing(getFieldsFromValidationError(doc), apiErrorDetails(doc));
        return;
      }
      if (doc === null) {
        onApiError();
        return;
      }
      navigate(paths.construct.docDetail(originalDocument!.protocol));
    } else {
      const payload = await prepareForUpload(docPayload);
      // @ts-expect-error err
      const createdDoc = await docStore.upload(payload, file!);
      if (createdDoc instanceof FieldValidationError) {
        onFieldsMissing(getFieldsFromValidationError(createdDoc), apiErrorDetails(createdDoc));
        return;
      }
      if (createdDoc === null) {
        onApiError();
        return;
      }
      setNotificationData({
        title: "Document was uploaded successfully",
        kind: "success",
      });
      navigate(paths.construct.docDetail(createdDoc.protocol));
    }
  };

  const [confirmationPayload, setConfirmationPayload] = useState<DocPayload | null>(null);
  const showConfirmationModal = confirmationPayload !== null;
  const startUpload = () => {
    setConfirmationPayload(docPayloadFromEditable(docPayload));
  };
  const updateState = (doc: Partial<Document>) => {
    const np: EditableDocPayload = { ...doc };
    if (np.archived) {
      np.expiration_date = undefined;
    }
    setDocPayload(np);
  };

  const storeDoc = editedId !== undefined ? docStore.getDocByProto(editedId) : null;
  // Seed the form from the store once per document: later reloads of the same document
  // (a relation was added/removed) must not throw away the user's unsaved field edits.
  useEffect(() => {
    if (storeDoc === null) return;
    if (syncedProtoRef.current === storeDoc.protocol) return;
    syncedProtoRef.current = storeDoc.protocol;
    updateState(storeDoc);
  }, [storeDoc]);

  // Relations are changed through their own API calls, which reload the document in the
  // store; pull the fresh list into both copies so the panel updates and the diff sent on
  // save stays limited to the form fields.
  const onRelationsChanged = useCallback(() => {
    const doc = editedId !== undefined ? docStore.getDocByProto(editedId) : null;
    if (doc === null) return;
    setOriginalDocument((prev) => (prev ? ({ ...prev, related: doc.related } as Document) : prev));
    setDocPayload((prev) => ({ ...prev, related: doc.related }));
  }, [editedId]);

  const [loadingEditedDocument, setLoadingEditedDocument] = useState<boolean>(!!editing);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const loadDocToEdit = async (proto: string) => {
    setLoadingEditedDocument(true);
    const doc = await docStore.loadDocument(proto);
    if (doc === null) return;
    setOriginalDocument(doc!);
    doc.types = [];
    if (doc.type) doc.types.push(doc.type);
    if (doc.secondary_type) doc.types.push(doc.secondary_type);
    const docVersions = await docStore.loadDocumentVersions(doc.id);
    if (docVersions.length !== 0) {
      const lastVersion = docVersions[docVersions.length - 1];
      const f = new File([], lastVersion.uuid);
      setFile(f);
      setOriginalFile(f);
    }
    updateState(doc);
    setLoadingEditedDocument(false);
  };

  useEffect(() => {
    if (editedId === undefined) return;
    loadDocToEdit(editedId);
    tagStore.f.load("", null);
  }, [editedId]);

  const onDocChange = useCallback(
    (key: SingleOnChangeKeys, value: any) => {
      setDocPayload((prev) => {
        return docPayloadFromFormChange(prev, key as keyof SingleFormValue, value);
      });
    },
    [docPayload]
  );

  const formValue: SingleFormValue = useMemo(() => formValueFromState(docPayload), [docPayload]);

  const typesIncludesGovernanceBody = useMemo(() => docPayload.types?.includes(20) || false, [docPayload.types]);
  const schema = useMemo(() => {
    return editing ? editSchema : uploadSchema;
  }, [typesIncludesGovernanceBody, editing]);

  const state = useMemo(() => ({ file: file, docPayload }), [file, docPayload]);
  const validationErrors = useMemo(() => {
    if (editing && loadingEditedDocument) {
      return new Map();
    }
    return validate(schema, state);
  }, [schema, state, editing, loadingEditedDocument]);

  const valid = validationErrors.size === 0;
  const areThereValidationErrors = !valid;

  const canUpload = valid;

  const formProps = useFormPropsFromState(docPayload, validationErrors, editing);

  const langVerModal = useBooleanHash("translations");
  const relModal = useBooleanHash("relations");

  const match = useMatch(paths.match.uploadDocWithTemplate);
  const templateId = match?.params.templateId;
  useEffect(() => {
    (async () => {
      if (templateId === undefined) return;
      setLoadingTemplate(true);
      const template = await templateStore.getTemplate(templateId, true);
      setLoadingTemplate(false);
      if (template === null) return;
      updateState({
        ...template.json_template,
        publication_date: template.json_template.publication_date || moment().toISOString(),
        release_date: template.json_template.release_date || moment().toISOString(),
      });
    })();
  }, [templateId]);

  const matchSimilarUpload = useMatch(paths.match.uploadSimilar);
  const similarDocId = matchSimilarUpload?.params.documentId;
  const uploadingSimilar = similarDocId ?? false;
  useEffect(() => {
    (async () => {
      if (similarDocId === undefined) return;
      const similarDoc: DocPayload | null = toJS(await docStore.loadDocument(similarDocId));
      if (similarDoc === null) return;
      delete similarDoc["title"];
      delete similarDoc["id"];
      delete similarDoc["protocol"];
      delete similarDoc["number"];
      delete similarDoc["uuid"];
      delete similarDoc["translations"];
      delete similarDoc["trans_group"];
      delete similarDoc["versions"];
      delete similarDoc["object_permission"];
      delete similarDoc["file_info"];
      delete similarDoc["trans_group_id"];
      delete similarDoc["uploader"];
      delete similarDoc["release_date"];
      const isReleaseDatePast = moment(similarDoc["release_date"]).isBefore(moment(new Date()));
      const types = [];
      if (similarDoc.type) {
        types.push(similarDoc.type);
      }
      if (similarDoc.secondary_type) {
        types.push(similarDoc.secondary_type);
      }
      if (similarDoc.organizational_unit) {
        similarDoc.organizational_unit = (similarDoc.organizational_unit as any).id;
      }
      updateState({
        ...similarDoc,
        types,
        publication_date: moment().toISOString(),
        release_date:
          isReleaseDatePast || !similarDoc["release_date"]
            ? moment(new Date()).toISOString()
            : similarDoc["release_date"],
      });
    })();
  }, [similarDocId]);

  const helpModal = useModal();
  useEffect(() => {
    if (validationErrors.size === 0) helpModal.close();
  }, [validationErrors]);

  const permissionsReady = authStore.userInfo?.permissions !== undefined;
  const loading = loadingEditedDocument || loadingTemplate || !permissionsReady;

  // Check if user has permission to access this page.
  const canAccessPage = editing ? hasChangePermission : hasUploadPermission;
  // For editing, also check document-level permissions once loaded.
  const canEditDocument = originalDocument ? docStore.canEdit(originalDocument) : true;
  const hasRequiredPermissions = canAccessPage && (!editing || canEditDocument);

  // Redirect if no permission
  useEffect(() => {
    if (!loading && !hasRequiredPermissions) {
      navigate("/");
    }
  }, [loading, hasRequiredPermissions, navigate]);

  useEffect(() => {
    if (!typesIncludesGovernanceBody) {
      setDocPayload((prev) => ({ ...prev, instance: undefined }));
    }
  }, [typesIncludesGovernanceBody]);

  useEffect(() => {
    docStore.uploadStatus = ReqStatus.Initial;
    docStore.editStatus = ReqStatus.Initial;
  }, []);

  // Don't render form if user can't access page
  if (!canAccessPage && !loading) {
    return (
      <Page title="Access Denied">
        <div className="flex items-center fjc h-full">
          <div>You do not have permission to access this page.</div>
        </div>
      </Page>
    );
  }

  return (
    <WithAutoScrollingWrapper>
      <Page title="Upload document" className="upload-document">
        {loading ? (
          <UploadPageSkeleton />
        ) : (
          <>
          <HelpModal errors={validationErrors} controller={helpModal} />
          <PageBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/#">
                    <Home className="size-4" />
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                {editing ? (
                  <>
                    {originalDocument && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href={paths.construct.docDetail(originalDocument.protocol)}>
                            Document {originalDocument?.protocol}
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                      </>
                    )}
                    <BreadcrumbItem>
                      <BreadcrumbPage>Edit</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                ) : uploadingSimilar ? (
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      <span>Upload document</span> (using <i className="italic">{similarDocId}</i>)
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                ) : (
                  <BreadcrumbItem>
                    <BreadcrumbPage>Upload documents</BreadcrumbPage>
                  </BreadcrumbItem>
                )}
              </BreadcrumbList>
            </Breadcrumb>
          </PageBreadcrumbs>

          <ConfirmationModal
            open={showConfirmationModal}
            docPayload={docPayload}
            submitting={isSubmitting}
            editing={editing}
            onAgree={() =>
              withLoading(async () => {
                await upload();
                setConfirmationPayload(null);
              }, setIsSubmitting)
            }
            close={() => setConfirmationPayload(null)}
          />

          <div className={cn([styles.uploadForm, "h-full w-full"])}>
            <div className="flex h-full w-full flex-col xl:flex-row">
              <div className={cn("w-full xl:w-1/3 2xl:w-1/4", styles.leftCol)}>
                <div className="w-full">
                  <div className="h-full">
                    <div>
                      <FileDropArea value={file !== null ? [file] : []} onAttachFile={(files) => setFile(files[0])} />
                    </div>
                    <div>
                      {validationErrors.get("file") !== undefined && (
                        <div className="text-xs [color:var(--support-01)]">
                          {(validationErrors.get("file")! as any).join(", ")}
                        </div>
                      )}
                      {editing && (
                        <div className="mt-3">
                          <div className={styles.leftColSection}>
                            <RelatedDocumentsSelect
                              docs={docPayload!.related! ?? []}
                              doc={docPayload as any as Document}
                              showModal={relModal.value}
                              setShowModal={relModal.setValue}
                              onChanged={onRelationsChanged}
                            />
                          </div>
                          <div className={styles.leftColSection}>
                            <DocumentLangVersionSelect
                              doc={docPayload as any as Document}
                              showModal={langVerModal.value}
                              setShowModal={langVerModal.setValue}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={cn("w-full xl:w-2/3 2xl:w-1/2", styles.formContainer)}>
                <div className="mb-6">
                  <DocumentForm
                    value={formValue}
                    onChange={onDocChange}
                    typesIncludesGovernanceBody={typesIncludesGovernanceBody}
                    multipleDocs={false}
                    formProps={formProps}
                    docTypeSelectProps={{
                      maxOptionsToSelect: DOC_TYPES_TO_SELECT_FOR_DOCUMENT_CREATION,
                    }}
                  />
                </div>
                <div className="space-x-4">
                  <Button
                    variant="default"
                    disabled={!canUpload}
                    title={
                      !canUpload
                        ? "You need to correct all errors or have pending changes to submit the form"
                        : undefined
                    }
                    onClick={startUpload}
                  >
                    {editing ? "Save document" : "Upload document"}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={!areThereValidationErrors && !originalDocument?.archived}
                    title={!canUpload ? "Show the reasons why you can't submit your form" : undefined}
                    onClick={helpModal.open}
                  >
                    Why can't I submit?
                    <CircleHelp />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          </>
        )}
      </Page>
    </WithAutoScrollingWrapper>
  );
});

export default UploadPage;
